import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CircleNotch, DownloadSimple, Plus } from '@phosphor-icons/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { BUSINESS_TABS, type BusinessRow, type BusinessTab } from '@mybusinessmate/schema';
import { AssignAmDialog } from '../../../../../components/businesses/assign-am-dialog';
import {
  BusinessRow as Row,
  BusinessRowsHeader,
  BusinessRowSkeleton,
} from '../../../../../components/businesses/business-row';
import { DirectoryHeader } from '../../../../../components/directory/directory-header';
import { EmptyState } from '../../../../../components/ui/empty-state';
import { InfiniteList } from '../../../../../components/ui/infinite-list';
import { useToast } from '../../../../../components/ui/toast';
import { FilterSelectPill, PillButton, ToolbarSearch } from '../../../../../components/ui/toolbar-controls';
import { UnderlineTabs, type UnderlineTab } from '../../../../../components/ui/underline-tabs';
import { Icon } from '../../../../../components/Icon';
import { accountManagerCandidatesQuery, businessListQuery } from '../../../../../lib/directory-api';
import { requireBusinessAccess } from '../../../../../lib/guards';
import { useSession } from '../../../../../lib/session';
import { useDebouncedValue } from '../../../../../lib/use-debounced-value';
import { NoResults } from '../../../../../components/ui/state-views';

export const Route = createFileRoute('/subdomain/admin/_staff/businesses/')({
  beforeLoad: ({ context }) => requireBusinessAccess(context.queryClient),
  // Filters live in the URL so a reload or a shared link shows the same list. `am` is a staff id, or "none".
  validateSearch: (search: Record<string, unknown>): { tab?: BusinessTab; q?: string; am?: string } => ({
    ...((BUSINESS_TABS as readonly unknown[]).includes(search.tab) ? { tab: search.tab as BusinessTab } : {}),
    ...(typeof search.q === 'string' && search.q ? { q: search.q } : {}),
    ...(typeof search.am === 'string' && search.am ? { am: search.am } : {}),
  }),
  // The tables are 1200 wide in the design (same canvas as the settings pages).
  staticData: { wide: true },
  component: BusinessesPage,
});

const COMING_SOON = {
  title: 'Coming soon',
  description: 'Plan, status and renewal filters arrive with billing.',
} as const;

/**
 * The business directory (Figma A03.1): an admin sees every business, an account manager only their own.
 * Anything the design shows that billing must supply (the Paying / Grace period / Cancelling / First Chai views, plan,
 * status, industry, city and joined filters, renewal sorting, CSV export) is drawn as designed and inert.
 */
function BusinessesPage() {
  const { tab = 'all', q, am } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const toast = useToast();
  const { data: session } = useSession();
  const isAdmin = session?.user.role === 'admin';
  const [text, setText] = useState(q ?? '');
  const [assigning, setAssigning] = useState<BusinessRow | null>(null);
  const search = useDebouncedValue(text.trim(), 250) || undefined;
  if (search !== q) void navigate({ search: (prev) => ({ ...prev, q: search }), replace: true });

  const list = useInfiniteQuery(businessListQuery({ tab, q: search, amId: am }));
  const managers = useQuery({ ...accountManagerCandidatesQuery, enabled: isAdmin });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const counts = list.data?.pages[0]?.counts;
  const filtered = !!search || !!am || tab !== 'all';
  const soon = () => toast({ ...COMING_SOON, tone: 'info' });

  const description = !counts
    ? undefined
    : isAdmin
      ? `${counts.all} ${counts.all === 1 ? 'business' : 'businesses'} · ${counts.needsAm} without an account manager`
      : `${counts.all} ${counts.all === 1 ? 'client' : 'clients'}`;

  const tabs: UnderlineTab<BusinessTab | 'paying' | 'grace' | 'cancelling' | 'first_chai'>[] = [
    { value: 'all', label: 'All', count: counts?.all },
    { value: 'paying', label: 'Paying', unavailable: true },
    { value: 'needs_am', label: 'Needs an AM', count: counts?.needsAm },
    { value: 'grace', label: 'Grace period', unavailable: true },
    { value: 'cancelling', label: 'Cancelling', unavailable: true },
    { value: 'first_chai', label: 'First Chai', unavailable: true },
  ];

  return (
    <>
      <DirectoryHeader
        title="Businesses"
        description={description}
        actions={
          <PillButton label="Export CSV" caret={false} icon={DownloadSimple} onClick={soon}>
            Export CSV
          </PillButton>
        }
      />

      {isAdmin ? (
        <UnderlineTabs
          aria-label="Views"
          tabs={tabs}
          value={tab}
          onChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, tab: next === 'all' ? undefined : (next as BusinessTab) }) })
          }
          onUnavailable={soon}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-(--mbm-space-2)">
        <ToolbarSearch
          aria-label="Search businesses"
          placeholder="Search name, owner, phone, GSTIN…"
          value={text}
          onChange={setText}
          className="w-full sm:w-(--mbm-layout-toolbar-search-width)"
        />
        <PillButton label="Plan" onClick={soon} />
        <PillButton label="Status" onClick={soon} />
        {isAdmin ? (
          <FilterSelectPill
            label="Account manager"
            value={am ?? ''}
            onChange={(value) => void navigate({ search: (prev) => ({ ...prev, am: value || undefined }) })}
            options={[
              { value: 'none', label: 'No account manager' },
              ...(managers.data ?? []).map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        ) : null}
        <PillButton label="Industry" onClick={soon} />
        <PillButton label="City" onClick={soon} />
        <PillButton label="Joined" onClick={soon} />
        <PillButton label="More filters" caret={false} icon={Plus} onClick={soon}>
          More
        </PillButton>
        <span className="ml-auto">
          {/* The list is newest first. The design's "Renewal date" order needs billing, so it is not claimed here. */}
          <PillButton label="Sort" onClick={soon}>
            Newest first
          </PillButton>
        </span>
      </div>

      <div className="overflow-hidden rounded-(--mbm-component-radius-card) bg-surface-card shadow-[var(--mbm-shadow-table-card),var(--mbm-shadow-sm)]">
        <BusinessRowsHeader />
        <InfiniteList
          items={items}
          getKey={(b) => b.id}
          renderItem={(b) => <Row business={b} canAssign={isAdmin} onAssign={() => setAssigning(b)} />}
          hasNextPage={list.hasNextPage}
          isFetchingNextPage={list.isFetchingNextPage}
          fetchNextPage={() => void list.fetchNextPage()}
          isLoading={list.isLoading}
          isError={list.isError}
          onRetry={() => void list.refetch()}
          emptyState={
            filtered ? (
              <NoResults
                query={search}
                description="Try another tab, filter or search."
                onClear={() => {
                  setText('');
                  void navigate({ search: { tab: undefined, q: undefined, am: undefined } });
                }}
              />
            ) : (
              <EmptyState
                title={isAdmin ? 'No businesses yet' : 'No clients assigned to you yet'}
                description={
                  isAdmin
                    ? 'A business appears here once a client has finished onboarding.'
                    : 'Ask an admin to assign you a client.'
                }
              />
            )
          }
          skeletonRow={<BusinessRowSkeleton />}
          skeletonCount={2}
        />
        {list.hasNextPage || list.isFetchingNextPage ? (
          <p className="flex items-center justify-center gap-(--mbm-space-2) py-(--mbm-space-3-5) text-body-small text-text-tertiary">
            <Icon icon={CircleNotch} size="sm" />
            Loading more{counts && !filtered ? ` · showing ${items.length} of ${counts.all}` : ''}
          </p>
        ) : null}
      </div>

      {assigning ? (
        <AssignAmDialog
          open
          onClose={() => setAssigning(null)}
          businessId={assigning.id}
          businessName={assigning.name}
          place={assigning.city}
          currentPrimaryId={assigning.accountManager?.id}
          hasNoManager={!assigning.accountManager}
        />
      ) : null}
    </>
  );
}
